import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { api, setUnauthorizedHandler } from './api';

export interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  /** May create invite links (admins always can). */
  canInvite: boolean;
  /** May create public share links (admins always can). */
  canShare: boolean;
  /** Storage allowance in bytes after the server default is applied; 0 = unlimited. */
  effectiveQuotaBytes: number;
}

interface AuthState {
  status: 'loading' | 'anon' | 'authed';
  user: User | null;
  /** True when the server has no users yet: the first registration needs no invite. */
  setupNeeded: boolean;
}

export const authStore = createStore<AuthState>(() => ({ status: 'loading', user: null, setupNeeded: false }));

setUnauthorizedHandler(() => {
  if (authStore.getState().status === 'authed') authStore.setState({ status: 'anon', user: null });
});

export async function loadAuth(): Promise<void> {
  try {
    const r = await api.get<{ user: User | null; setupNeeded: boolean }>('/auth/me');
    authStore.setState({ status: r.user ? 'authed' : 'anon', user: r.user, setupNeeded: r.setupNeeded });
  } catch {
    authStore.setState({ status: 'anon', user: null });
  }
}

export async function login(email: string, password: string): Promise<void> {
  const r = await api.post<{ user: User }>('/auth/login', { email, password });
  authStore.setState({ status: 'authed', user: r.user, setupNeeded: false });
}

export async function register(input: {
  email: string;
  password: string;
  name: string;
  invite: string | null;
}): Promise<void> {
  const r = await api.post<{ user: User }>('/auth/register', input);
  authStore.setState({ status: 'authed', user: r.user, setupNeeded: false });
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    authStore.setState({ status: 'anon', user: null });
  }
}

export function useAuth<T>(selector: (s: AuthState) => T): T {
  return useStore(authStore, selector);
}
